import React, { useState, useEffect } from 'react';
import apiService from '../services/ApiService';
import {
  LinkIcon,
  CheckIcon,
  XMarkIcon,
  Cog6ToothIcon,
  QrCodeIcon,
  ChartBarIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  PhotoIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import { FaTwitter, FaFacebook, FaReddit, FaInstagram } from 'react-icons/fa';
import { withShowcaseDisplayUrl } from '../hooks/useShowcaseMode';
import { downloadIdentityCard, type IdentityCardFormat } from '../utils/identityCardDownload';
import VehicleCertificationPanel from './VehicleCertificationPanel';
import ShareCardGenerator, {
  type ShareCardAward,
  type ShareCardVehicleInfo,
} from './share/ShareCardGenerator';
import QrStickerGenerator from './share/QrStickerGenerator';
import type { Achievement, BuildStat, PerformanceRecord } from '../types/buildExpansion';

// Cast icon components to be compatible with React 19's stricter types
const TwitterIcon = FaTwitter as React.ElementType;
const FacebookIcon = FaFacebook as React.ElementType;
const RedditIcon = FaReddit as React.ElementType;
const InstagramIcon = FaInstagram as React.ElementType;

export type SharingModalTabId =
  | 'settings'
  | 'qr'
  | 'analytics'
  | 'share-card'
  | 'qr-sticker';

/** Owner-only share card / QR sticker tools — shown only in the QR-icon Share modal. */
export interface SharingModalOwnerTools {
  enabled: boolean;
  shareUrl: string;
  qrImageUrl: string | null;
  onEnsureQrImage?: () => Promise<string | null>;
  vehicle: ShareCardVehicleInfo;
  ownerName: string;
  imageUrl?: string | null;
  stats?: BuildStat[];
  achievements?: Achievement[];
  records?: PerformanceRecord[];
  awards?: ShareCardAward[];
  investedAmount?: number | null;
}

interface VehicleSharingModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicleId: number;
  vehicleName: string;
  /** `panel` embeds sharing controls inside Manage build without a nested dialog. */
  variant?: 'modal' | 'panel';
  /** Notify parent when public visibility changes so other Manage panels stay in sync. */
  onPublicChange?: (isPublic: boolean) => void;
  /**
   * Share card + QR sticker sections. Only rendered for `variant="modal"`
   * (QR icon entry) so public visitors never see these tools.
   */
  ownerTools?: SharingModalOwnerTools | null;
}

interface SharingData {
  vehicle: {
    id: number;
    year: number;
    make: string;
    model: string;
    trim: string;
    nickname: string;
    is_public: boolean;
    public_slug: string;
    public_url: string;
    share_count: number;
    view_count: number;
  };
  settings: {
    show_modifications: boolean;
    show_performance: boolean;
    show_images: boolean;
    show_contact_info: boolean;
    show_social_links: boolean;
    custom_message: string;
    theme_color: string;
  };
  stats: {
    total_views: number;
    total_shares: number;
    last_viewed: string;
    last_shared: string;
    share_breakdown: Array<{ share_type: string; count: number; last_occurrence: string }>;
    recent_activity: Array<{ date: string; share_type: string; count: number }>;
    social_platform_breakdown?: Array<{ referrer: string; count: number }>;
  };
  qr_codes: Array<{
    id: number;
    format: string;
    size: string;
    file_path: string;
    download_count: number;
    created_at: string;
  }>;
}

const VehicleSharingModal: React.FC<VehicleSharingModalProps> = ({
  isOpen,
  onClose,
  vehicleId,
  vehicleName,
  variant = 'modal',
  onPublicChange,
  ownerTools = null,
}) => {
  const [activeTab, setActiveTab] = useState<SharingModalTabId>('settings');
  const [sharingData, setSharingData] = useState<SharingData | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showInstagramModal, setShowInstagramModal] = useState(false);
  const [savingKeys, setSavingKeys] = useState<Record<string, boolean>>({});
  const [savedKeys, setSavedKeys] = useState<Record<string, boolean>>({});
  const [resolvedQrUrl, setResolvedQrUrl] = useState<string | null>(null);

  // Owner export tools only appear in the QR-icon modal, never in Manage panel or for guests.
  const showOwnerToolTabs = variant === 'modal' && Boolean(ownerTools?.enabled);

  // Load sharing data when modal opens
  useEffect(() => {
    if (isOpen && vehicleId) {
      loadSharingData();
      setActiveTab('settings');
      setResolvedQrUrl(ownerTools?.qrImageUrl ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when modal opens
  }, [isOpen, vehicleId]);

  useEffect(() => {
    if (ownerTools?.qrImageUrl) {
      setResolvedQrUrl(ownerTools.qrImageUrl);
    }
  }, [ownerTools?.qrImageUrl]);

  useEffect(() => {
    if (!isOpen || !showOwnerToolTabs) return;
    if (activeTab !== 'share-card' && activeTab !== 'qr-sticker') return;
    if (resolvedQrUrl) return;
    if (!ownerTools?.onEnsureQrImage) return;
    let cancelled = false;
    void ownerTools.onEnsureQrImage().then((url) => {
      if (!cancelled && url) setResolvedQrUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, showOwnerToolTabs, activeTab, resolvedQrUrl, ownerTools]);

  const loadSharingData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getVehicleSharingSettings(vehicleId);
      setSharingData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load sharing settings');
    } finally {
      setLoading(false);
    }
  };

  const refreshSharingData = async () => {
    await loadSharingData();
  };

  const generateQRCode = async (options = {}) => {
    setGenerating(true);
    setError(null);
    try {
      // Always use large size and SVG format for simplicity
      await apiService.generateQRCode(vehicleId, { format: 'svg', size: 'large', ...options });

      // Refresh sharing data to get updated QR codes list
      await loadSharingData();
    } catch (err: any) {
      setError(err.message || 'Failed to generate QR code');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const shareVia = (platform: string) => {
    if (!sharingData?.vehicle.public_url) return;
    
    // Append platform-specific ref so backend can distinguish source (e.g., social_twitter)
    const refParam = `social_${platform}`;
    const url = sharingData.vehicle.public_url.includes('?')
      ? `${sharingData.vehicle.public_url}&ref=${refParam}`
      : `${sharingData.vehicle.public_url}?ref=${refParam}`;
    const text = `Check out my ${sharingData.vehicle.year} ${sharingData.vehicle.make} ${sharingData.vehicle.model} on Bilt!`;
    
    let shareUrl = '';
    
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'instagram':
        // Show Instagram sharing instructions modal
        setShowInstagramModal(true);
        return;
      case 'reddit':
        shareUrl = `https://reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(text)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const handleUpdateSharing = async (patch: {
    is_public?: boolean;
    show_modifications?: boolean;
    show_performance?: boolean;
    show_images?: boolean;
    show_contact_info?: boolean;
    show_social_links?: boolean;
    custom_message?: string;
  }) => {
    if (!sharingData) return;

    const saveKey = Object.keys(patch)[0] ?? 'settings';
    const previousData = sharingData;

    setSavingKeys((prev) => ({ ...prev, [saveKey]: true }));
    setSavedKeys((prev) => ({ ...prev, [saveKey]: false }));
    setError(null);

    setSharingData((prev) =>
      prev
        ? {
            ...prev,
            vehicle:
              patch.is_public !== undefined
                ? { ...prev.vehicle, is_public: patch.is_public }
                : prev.vehicle,
            settings: { ...prev.settings, ...patch },
          }
        : null
    );

    try {
      const merged = { ...previousData.settings, ...patch };
      const result = await apiService.updateVehicleSharingSettings(vehicleId, {
        is_public: patch.is_public ?? previousData.vehicle.is_public,
        show_modifications: merged.show_modifications,
        show_performance: merged.show_performance,
        show_images: merged.show_images,
        show_contact_info: merged.show_contact_info,
        show_social_links: merged.show_social_links,
        custom_message: merged.custom_message ?? undefined,
        theme_color: previousData.settings.theme_color,
      });
      setSharingData((prev) =>
        prev
          ? {
              ...prev,
              vehicle: {
                ...prev.vehicle,
                is_public: result.vehicle.is_public,
                public_slug: result.vehicle.public_slug,
                public_url: result.vehicle.public_url ?? '',
              },
              settings: { ...prev.settings, ...result.settings },
            }
          : null
      );
      setSavedKeys((prev) => ({ ...prev, [saveKey]: true }));
      window.setTimeout(() => {
        setSavedKeys((prev) => ({ ...prev, [saveKey]: false }));
      }, 1500);
      if (typeof patch.is_public === 'boolean') {
        onPublicChange?.(result.vehicle.is_public);
      }
    } catch (err: unknown) {
      setSharingData(previousData);
      setError(err instanceof Error ? err.message : 'Failed to update sharing settings');
    } finally {
      setSavingKeys((prev) => ({ ...prev, [saveKey]: false }));
    }
  };

  const isSavingAny = Object.values(savingKeys).some(Boolean);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const body = (
    <>
      {error && (
        <div className="bg-red-900/50 border-l-4 border-red-500 p-4 m-4 rounded">
          <p className="text-sm text-red-200">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
        </div>
      ) : !sharingData ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center text-gray-400">
            <p>Could not load sharing data.</p>
            <button
              type="button"
              onClick={loadSharingData}
              className="mt-2 text-orange-400 hover:text-orange-300"
            >
              Retry
            </button>
          </div>
        </div>
      ) : (
        <div className={`flex flex-col ${variant === 'panel' ? 'gap-4' : 'md:flex-row'}`}>
          <div
            className={`w-full ${
              variant === 'panel' ? '' : 'md:w-1/4 border-b md:border-b-0 md:border-r border-white/[0.08]'
            }`}
          >
            <nav
              className={`flex ${
                variant === 'panel'
                  ? 'flex-wrap gap-1'
                  : 'md:flex-col p-4 space-x-2 md:space-x-0 md:space-y-2'
              }`}
              aria-label="Sharing sections"
            >
              {(
                [
                  { id: 'settings' as SharingModalTabId, label: 'Share Link & Social', icon: Cog6ToothIcon },
                  { id: 'qr' as SharingModalTabId, label: 'QR Codes', icon: QrCodeIcon },
                  { id: 'analytics' as SharingModalTabId, label: 'Analytics', icon: ChartBarIcon },
                  ...(showOwnerToolTabs
                    ? [
                        { id: 'share-card' as SharingModalTabId, label: 'Share Card', icon: PhotoIcon },
                        { id: 'qr-sticker' as SharingModalTabId, label: 'QR Sticker', icon: PrinterIcon },
                      ]
                    : []),
                ]
              ).map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center ${variant === 'panel' ? 'px-3 py-2' : 'w-full px-4 py-3'} rounded-lg text-left text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-[#141414] text-white'
                        : 'text-gray-300 hover:bg-white/[0.06] hover:text-white'
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${variant === 'panel' ? 'mr-2' : 'mr-3'}`} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          <div className={`w-full ${variant === 'panel' ? 'pt-1' : 'md:w-3/4 p-6'}`}>
            {activeTab === 'settings' && (
              <SettingsTab
                sharingData={sharingData}
                copyToClipboard={copyToClipboard}
                copySuccess={copySuccess}
                shareVia={shareVia}
                onUpdate={handleUpdateSharing}
                savingKeys={savingKeys}
                savedKeys={savedKeys}
                isSavingAny={isSavingAny}
              />
            )}
            {activeTab === 'qr' && (
              <QRTab
                generateQRCode={generateQRCode}
                generating={generating}
                sharingData={sharingData}
              />
            )}
            {activeTab === 'analytics' && (
              <AnalyticsTab
                sharingData={sharingData}
                onRefresh={refreshSharingData}
                loading={loading}
              />
            )}
            {showOwnerToolTabs && ownerTools && activeTab === 'share-card' && (
              <ShareCardGenerator
                variant="panel"
                isOpen
                onClose={() => setActiveTab('analytics')}
                vehicle={ownerTools.vehicle}
                shareUrl={ownerTools.shareUrl}
                vehicleId={vehicleId}
                ownerName={ownerTools.ownerName}
                imageUrl={ownerTools.imageUrl}
                qrImageUrl={resolvedQrUrl || ownerTools.qrImageUrl}
                stats={ownerTools.stats}
                achievements={ownerTools.achievements}
                records={ownerTools.records}
                awards={ownerTools.awards}
                investedAmount={ownerTools.investedAmount}
              />
            )}
            {showOwnerToolTabs && ownerTools && activeTab === 'qr-sticker' && (
              <QrStickerGenerator
                variant="panel"
                isOpen
                onClose={() => setActiveTab('analytics')}
                vehicleName={vehicleName}
                shareUrl={ownerTools.shareUrl}
                qrImageUrl={resolvedQrUrl || ownerTools.qrImageUrl || ''}
                vehicleId={vehicleId}
                vehicleImageUrl={ownerTools.imageUrl}
              />
            )}
          </div>
        </div>
      )}
    </>
  );

  const instagramOverlay = showInstagramModal ? (
    <div className="modal-overlay z-[60]">
      <div className="modal-panel-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white flex items-center">
            <InstagramIcon className="w-6 h-6 mr-2 text-orange-500" />
            Share to Instagram
          </h3>
          <button
            type="button"
            onClick={() => setShowInstagramModal(false)}
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Close Instagram share help"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          <p className="text-gray-300 text-sm">
            Instagram doesn&apos;t support direct web sharing. Copy the text below, then paste it in Instagram.
          </p>

          <div className="surface rounded-lg p-4">
            <div className="space-y-3 text-sm">
              <div className="flex items-start space-x-3">
                <span className="bg-[#111] text-white rounded-md w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  1
                </span>
                <span className="text-gray-300">Copy the text below by clicking the button</span>
              </div>
              <div className="flex items-start space-x-3">
                <span className="bg-[#111] text-white rounded-md w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  2
                </span>
                <span className="text-gray-300">Open Instagram on your phone or go to instagram.com</span>
              </div>
              <div className="flex items-start space-x-3">
                <span className="bg-[#111] text-white rounded-md w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  3
                </span>
                <span className="text-gray-300">Create a new post or story and paste the text</span>
              </div>
            </div>
          </div>

          <div className="bg-[#0c0c0c]/50 rounded-lg p-3">
            <p className="text-gray-400 text-xs mb-2">Content to copy:</p>
            <p className="text-gray-200 text-sm break-words">
              {sharingData &&
                `Check out my ${sharingData.vehicle.year} ${sharingData.vehicle.make} ${sharingData.vehicle.model} on Bilt! ${
                  sharingData.vehicle.public_url.includes('?')
                    ? `${sharingData.vehicle.public_url}&ref=social_instagram`
                    : `${sharingData.vehicle.public_url}?ref=social_instagram`
                }`}
            </p>
          </div>

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => {
                if (sharingData) {
                  const text = `Check out my ${sharingData.vehicle.year} ${sharingData.vehicle.make} ${sharingData.vehicle.model} on Bilt!`;
                  const igLink = sharingData.vehicle.public_url.includes('?')
                    ? `${sharingData.vehicle.public_url}&ref=social_instagram`
                    : `${sharingData.vehicle.public_url}?ref=social_instagram`;
                  copyToClipboard(`${text}\n\n${igLink}`);
                }
              }}
              className="flex-1 px-4 py-2 btn-primary font-medium transition-colors flex items-center justify-center"
            >
              {copySuccess ? (
                <>
                  <CheckIcon className="w-4 h-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Copy Text
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => setShowInstagramModal(false)}
              className="btn-secondary"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  if (variant === 'panel') {
    return (
      <>
        <div className="space-y-2">
          <p className="sr-only">Sharing settings for {vehicleName}</p>
          {body}
        </div>
        {instagramOverlay}
      </>
    );
  }

  return (
    <>
      <div className="modal-overlay" onClick={handleBackdropClick} role="presentation">
        <div className="modal-panel max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between p-6 border-b border-white/[0.08] flex-shrink-0">
            <div>
              <h2 className="text-2xl font-bold text-white">Share</h2>
              <p className="text-gray-400">{vehicleName}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
              aria-label="Close sharing dialog"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          <div className="flex-grow overflow-y-auto">{body}</div>
        </div>
      </div>
      {instagramOverlay}
    </>
  );
};

const SharingToggle = ({
  label,
  description,
  checked,
  disabled,
  saveState = 'idle',
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  saveState?: 'idle' | 'saving' | 'saved';
  onChange: (value: boolean) => void;
}) => (
  <div className="flex items-center justify-between gap-4 py-3 border-b border-white/[0.10] last:border-0">
    <div>
      <p className="text-sm font-medium text-white">{label}</p>
      <p className="text-xs text-gray-400 mt-0.5">{description}</p>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      {saveState === 'saving' && (
        <span className="text-xs text-orange-300/80" aria-live="polite">Saving…</span>
      )}
      {saveState === 'saved' && (
        <span className="text-xs text-green-400/90" aria-live="polite">Saved</span>
      )}
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
          checked ? 'toggle-track-on bg-emerald-600' : 'bg-[#1a1a1a]'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform mt-0.5 ${
            checked ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  </div>
);

const getSaveState = (
  key: string,
  savingKeys: Record<string, boolean>,
  savedKeys: Record<string, boolean>
): 'idle' | 'saving' | 'saved' => {
  if (savingKeys[key]) return 'saving';
  if (savedKeys[key]) return 'saved';
  return 'idle';
};

const SettingsTab = ({
  sharingData,
  copyToClipboard,
  copySuccess,
  shareVia,
  onUpdate,
  savingKeys,
  savedKeys,
  isSavingAny,
}: {
  sharingData: SharingData;
  copyToClipboard: (text: string) => void;
  copySuccess: boolean;
  shareVia: (platform: string) => void;
  onUpdate: (patch: Record<string, boolean | string | undefined>) => Promise<void>;
  savingKeys: Record<string, boolean>;
  savedKeys: Record<string, boolean>;
  isSavingAny: boolean;
}) => {
  const [messageDraft, setMessageDraft] = useState(sharingData.settings.custom_message || '');

  useEffect(() => {
    setMessageDraft(sharingData.settings.custom_message || '');
  }, [sharingData.settings.custom_message]);

  return (
  <div className="space-y-8">
    <div>
      <h3 className="text-xl font-semibold text-white mb-4">Public visibility</h3>
      <div className="surface rounded-lg p-4 space-y-1">
        <SharingToggle
          label="Make vehicle public"
          description="Anyone with the link can view your build page"
          checked={sharingData.vehicle.is_public}
          disabled={isSavingAny}
          saveState={getSaveState('is_public', savingKeys, savedKeys)}
          onChange={(value) => onUpdate({ is_public: value })}
        />
        {!sharingData.vehicle.is_public && (
          <p className="text-sm text-amber-300/90 pt-2">
            Public visibility is needed for a share link and QR code.
          </p>
        )}
      </div>
    </div>

    <VehicleCertificationPanel
      vehicleId={sharingData.vehicle.id}
      isPublic={sharingData.vehicle.is_public}
    />

    {sharingData.vehicle.is_public && sharingData.vehicle.public_url && (
      <div>
        <h3 className="text-xl font-semibold text-white mb-4">Share link</h3>
        <div className="surface rounded-lg p-4">
          <div className="flex">
            <input
              type="text"
              readOnly
              value={sharingData.vehicle.public_url}
              className="input rounded-r-none"
              aria-label="Public vehicle URL"
            />
            <button
              type="button"
              onClick={() => copyToClipboard(sharingData.vehicle.public_url)}
              className="px-4 py-2 btn-primary rounded-r-lg font-medium transition-colors flex items-center"
              aria-label="Copy share link"
            >
              {copySuccess ? <CheckIcon className="w-5 h-5" /> : <LinkIcon className="w-5 h-5" />}
            </button>
          </div>
          <div className="flex items-center space-x-4 mt-4">
            <p className="text-sm text-gray-400">Share on:</p>
            <div className="flex space-x-2">
              <button type="button" onClick={() => shareVia('twitter')} className="p-2 text-gray-400 hover:text-white transition-colors" aria-label="Share on Twitter"><TwitterIcon size={20} /></button>
              <button type="button" onClick={() => shareVia('facebook')} className="p-2 text-gray-400 hover:text-white transition-colors" aria-label="Share on Facebook"><FacebookIcon size={20} /></button>
              <button type="button" onClick={() => shareVia('instagram')} className="p-2 text-gray-400 hover:text-white transition-colors" aria-label="Share on Instagram"><InstagramIcon size={20} /></button>
              <button type="button" onClick={() => shareVia('reddit')} className="p-2 text-gray-400 hover:text-white transition-colors" aria-label="Share on Reddit"><RedditIcon size={20} /></button>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-white/[0.10]">
          <h4 className="text-base font-semibold text-white mb-2">iPad display link</h4>
          <p className="text-sm text-gray-400 mb-3">
            Use this URL on an iPad at your car show stand: larger photos, touch-friendly mods, and no sign-up prompts.
          </p>
          <div className="flex">
            <input
              type="text"
              readOnly
              value={withShowcaseDisplayUrl(sharingData.vehicle.public_url)}
              className="input rounded-r-none text-sm"
              aria-label="iPad display URL"
            />
            <button
              type="button"
              onClick={() => copyToClipboard(withShowcaseDisplayUrl(sharingData.vehicle.public_url))}
              className="px-4 py-2 btn-primary rounded-r-lg font-medium transition-colors flex items-center shrink-0"
              aria-label="Copy iPad display link"
            >
              {copySuccess ? <CheckIcon className="w-5 h-5" /> : <LinkIcon className="w-5 h-5" />}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Tip: enable Guided Access on the iPad so visitors stay on your build page.
          </p>
        </div>
      </div>
    )}

    <div>
      <h3 className="text-xl font-semibold text-white mb-4">What visitors see</h3>
      <div className="surface rounded-lg px-4">
        <SharingToggle
          label="Modifications"
          description="Show the mods list on your public page"
          checked={sharingData.settings.show_modifications}
          disabled={isSavingAny || !sharingData.vehicle.is_public}
          saveState={getSaveState('show_modifications', savingKeys, savedKeys)}
          onChange={(value) => onUpdate({ show_modifications: value })}
        />
        <SharingToggle
          label="Photos"
          description="Show hero and gallery images"
          checked={sharingData.settings.show_images}
          disabled={isSavingAny || !sharingData.vehicle.is_public}
          saveState={getSaveState('show_images', savingKeys, savedKeys)}
          onChange={(value) => onUpdate({ show_images: value })}
        />
        <SharingToggle
          label="Performance stats"
          description="Show horsepower, torque, and redline"
          checked={sharingData.settings.show_performance}
          disabled={isSavingAny || !sharingData.vehicle.is_public}
          saveState={getSaveState('show_performance', savingKeys, savedKeys)}
          onChange={(value) => onUpdate({ show_performance: value })}
        />
        <SharingToggle
          label="Contact info"
          description="Show ways to reach you on the public page"
          checked={sharingData.settings.show_contact_info}
          disabled={isSavingAny || !sharingData.vehicle.is_public}
          saveState={getSaveState('show_contact_info', savingKeys, savedKeys)}
          onChange={(value) => onUpdate({ show_contact_info: value })}
        />
        <SharingToggle
          label="Social links"
          description="Show linked social profiles"
          checked={sharingData.settings.show_social_links}
          disabled={isSavingAny || !sharingData.vehicle.is_public}
          saveState={getSaveState('show_social_links', savingKeys, savedKeys)}
          onChange={(value) => onUpdate({ show_social_links: value })}
        />
      </div>
    </div>

    {sharingData.vehicle.is_public && (
      <div>
        <h3 className="text-xl font-semibold text-white mb-4">Visitor message</h3>
        <textarea
          value={messageDraft}
          onChange={(e) => setMessageDraft(e.target.value)}
          onBlur={() => {
            const trimmed = messageDraft.trim();
            if (trimmed !== (sharingData.settings.custom_message || '')) {
              void onUpdate({ custom_message: trimmed });
            }
          }}
          disabled={isSavingAny}
          aria-describedby="custom-message-save-hint"
          rows={3}
          placeholder="Optional note for people viewing your public page…"
          className="input"
        />
        <p id="custom-message-save-hint" className="text-xs text-gray-500 mt-2">
          {getSaveState('custom_message', savingKeys, savedKeys) === 'saving'
            ? 'Saving message…'
            : getSaveState('custom_message', savingKeys, savedKeys) === 'saved'
              ? 'Message saved'
              : 'Saved when you click away from this field.'}
        </p>
      </div>
    )}
  </div>
  );
};

const QRTab = ({ generateQRCode, generating, sharingData }: any) => {
  const [cardBusy, setCardBusy] = useState<IdentityCardFormat | null>(null);
  const sortedQRCodes = [...sharingData.qr_codes].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  const currentQR = sortedQRCodes.find((qr: any) => qr.size === 'large') || sortedQRCodes[0];

  const handleGenerateQRCode = async () => {
    await generateQRCode({ ref: 'qr' });
  };

  const vehicleLabel =
    sharingData.vehicle.nickname ||
    `${sharingData.vehicle.year} ${sharingData.vehicle.make} ${sharingData.vehicle.model} ${sharingData.vehicle.trim || ''}`.trim();

  const owner = apiService.getCurrentUser();
  const ownerLabel = owner
    ? [owner.first_name, owner.last_name].filter(Boolean).join(' ') || owner.username
    : 'Owner';

  const handleDownloadCard = async (format: IdentityCardFormat) => {
    if (!currentQR?.file_path || !sharingData.vehicle.public_url) return;
    try {
      setCardBusy(format);
      await downloadIdentityCard(format, {
        vehicleLabel,
        ownerLabel,
        profileUrl: sharingData.vehicle.public_url,
        qrImageUrl: apiService.getFileUrl(currentQR.file_path) || '',
      });
    } catch (err: unknown) {
      console.error('Identity card download failed:', err);
    } finally {
      setCardBusy(null);
    }
  };

  const cardFormats: { format: IdentityCardFormat; label: string; desc: string }[] = [
    { format: 'placard', label: 'Show placard', desc: 'Portrait card for display stands' },
    { format: 'windshield', label: 'Windshield card', desc: 'Wide layout for dash or window' },
    { format: 'story', label: 'Social story', desc: '9:16 vertical for Instagram stories' },
  ];

  return (
    <div className="space-y-8">
      <div>
        <p className="kicker mb-2">Display at the show</p>
        <h3 className="text-lg font-semibold text-white mb-3">Identity cards</h3>
        <div className="form-section">
          <p className="text-sm text-gray-500 mb-5 leading-relaxed">
            Printable placards with your build name, owner credit, and QR code, designed for windshields and display stands.
          </p>
          {!currentQR?.file_path ? (
            <button
              type="button"
              onClick={handleGenerateQRCode}
              disabled={generating}
              className="w-full min-h-[48px] px-4 py-3 bg-white text-gray-900 rounded-xl font-semibold text-sm hover:bg-gray-100 disabled:opacity-50 transition-colors"
            >
              {generating ? 'Generating QR…' : 'Generate QR for identity cards'}
            </button>
          ) : (
            <div className="grid gap-2 sm:grid-cols-3">
              {cardFormats.map(({ format, label, desc }) => (
                <button
                  key={format}
                  type="button"
                  disabled={cardBusy !== null}
                  onClick={() => handleDownloadCard(format)}
                  className="card text-left min-h-[72px] px-4 py-3.5 hover:border-white/[0.14] disabled:opacity-50 transition-colors cursor-pointer"
                >
                  <p className="font-medium text-white text-sm">{label}</p>
                  <p className="text-xs text-gray-600 mt-1 leading-snug">{desc}</p>
                  {cardBusy === format && <p className="text-xs text-gray-400 mt-2">Preparing…</p>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-white mb-3">QR code</h3>
        <div className="form-section">
          <p className="text-sm text-gray-500 mb-4 leading-relaxed">
            Links directly to your public build profile. Optimized for phone cameras at the show.
          </p>
          <button
            type="button"
            onClick={handleGenerateQRCode}
            disabled={generating}
            className="w-full min-h-[44px] px-4 py-2.5 border border-white/[0.08] text-gray-200 rounded-xl font-medium text-sm hover:bg-white/[0.05] disabled:opacity-50 transition-colors"
          >
            {generating ? 'Generating…' : currentQR?.file_path ? 'Regenerate QR code' : 'Generate QR code'}
          </button>
        </div>
      </div>

      {currentQR?.file_path && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-3">Preview</h3>
          <div className="form-section text-center !p-6 sm:!p-8">
            <div className="inline-block bg-white p-4 rounded-xl mb-4 shadow-lg shadow-black/20">
              <img 
                src={apiService.getFileUrl(currentQR.file_path) || ''} 
                alt="QR Code" 
                className="w-48 h-48 object-contain"
              />
            </div>
            <div className="space-y-2">
              <p className="text-gray-400 text-sm">
                Scan this code to view your vehicle page
              </p>
              <a 
                href={apiService.getFileUrl(currentQR.file_path) || ''} 
                download={`${sharingData.vehicle.make}-${sharingData.vehicle.model}-QR.svg`}
                className="btn-secondary"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                <span>Download QR Code</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AnalyticsTab = ({ sharingData, onRefresh, loading }: any) => {
  const formatPlatform = (ref: string) => {
    if (!ref) return 'Unknown';
    const clean = ref.replace(/^social[_-]/i, '');
    return clean.charAt(0).toUpperCase() + clean.slice(1);
  };

  return (
  <div className="space-y-8">
    {!sharingData.vehicle.is_public ? (
       <div className="text-center py-12">
         <div className="text-gray-400 text-6xl mb-4">
           <ChartBarIcon className="inline-block" />
         </div>
         <h3 className="text-lg font-medium text-white mb-2">No Analytics Yet</h3>
         <p className="text-gray-400">
           Public vehicles can track views and shares.
         </p>
       </div>
    ) : (
      <>
          {/* Refresh Button */}
          <div className="flex justify-end">
            <button
              onClick={onRefresh}
              disabled={loading}
              className="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh analytics"
            >
              <ArrowPathIcon className={`w-5 h-5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        {/* Stats Overview */}
        <div>
          <h3 className="text-xl font-semibold text-white mb-4">Overview</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="surface rounded-lg p-4 text-center border border-white/[0.10]">
              <div className="text-3xl font-bold text-orange-400">{sharingData.stats.total_views}</div>
              <div className="text-sm text-gray-400">Total Views</div>
            </div>
            <div className="surface rounded-lg p-4 text-center border border-white/[0.10]">
              <div className="text-3xl font-bold text-orange-300">{sharingData.stats.total_shares}</div>
              <div className="text-sm text-gray-400">Total Shares</div>
            </div>
            <div className="surface rounded-lg p-4 text-center border border-white/[0.10]">
              <div className="text-3xl font-bold text-orange-400">
                {sharingData.stats.share_breakdown.find((s: any) => s.share_type === 'qr_scan')?.count || 0}
              </div>
              <div className="text-sm text-gray-400">QR Scans</div>
            </div>
            <div className="surface rounded-lg p-4 text-center border border-white/[0.10]">
              <div className="text-3xl font-bold text-orange-200">
                {sharingData.stats.share_breakdown.find((s: any) => s.share_type === 'social_media')?.count || 0}
              </div>
              <div className="text-sm text-gray-400">Social Shares</div>
            </div>
          </div>
        </div>

        {/* Share Breakdown */}
        <div className="surface rounded-lg p-4 border border-white/[0.10]">
          <h4 className="font-medium text-white mb-3">Share Breakdown</h4>
          <div className="space-y-2">
            {sharingData.stats.share_breakdown.map((share: any, index: number) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-gray-300 capitalize">
                  {share.share_type.replace('_', ' ')}
                </span>
                <span className="font-semibold text-white">{share.count}</span>
              </div>
            ))}
          </div>
            {/* Social platform breakdown */}
            {sharingData.stats.social_platform_breakdown?.length > 0 && (
              <div className="mt-4">
                <h5 className="text-sm font-medium text-gray-400 mb-2">Social Platforms</h5>
                <div className="space-y-1">
                  {sharingData.stats.social_platform_breakdown.map((sp: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-gray-300">{formatPlatform(sp.referrer)}</span>
                      <span className="text-gray-200">{sp.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>
      </>
    )}
  </div>
);
};

export default VehicleSharingModal; 